<template>
  <div class="message-page">
    <aside class="message-list">
      <div class="header">
        <h3>Meine Nachrichten</h3>
        <button @click="newMessage" class="primary-button">Neue Nachricht</button>
      </div>
      <ul>
        <li v-for="(msg, index) in messages" :key="index" @click="selectMessage(msg)"
          :class="{ 'message-to': msg.sender_id === parseInt(userId), 'message-from': msg.sender_id !== parseInt(userId) }">
          <p><small>{{ msg.created_at }}</small></p>
          <p>
            <strong>
              <span v-if="msg.sender_id === parseInt(userId)">To: {{ msg.recipient_name }}</span>
              <span v-else>From: {{ msg.sender_name }}</span>
            </strong>
          </p>
          <p>{{ msg.subject }}</p>
        </li>
      </ul>
    </aside>

    <section class="message-view">
      <div v-if="selectedMessage" class="message-details">
        <div class="message-field">
          <span class="label"><strong>Date:</strong></span>
          <span class="value">{{ selectedMessage.created_at }}</span>
        </div>
        <div class="message-field">
          <span class="label"><strong>To:</strong></span>
          <span class="value" v-if="!isReplying">
            {{ selectedMessage.recipient_id === parseInt(userId) ? 'You' : selectedMessage.recipient_name }}
          </span>
          <span class="value" v-else>
            {{ selectedMessage.sender_name }}
          </span>
        </div>
        <div class="message-field">
          <span class="label"><strong>From:</strong></span>
          <span class="value" v-if="!isReplying">
            {{ selectedMessage.sender_name }}
          </span>
          <span class="value" v-else>
            {{ getUserName(userId) }}
          </span>
        </div>
        <div class="message-field">
          <span class="label"><strong>Subject:</strong></span>
          <span class="value">{{ selectedMessage.subject }}</span>
        </div>
        <p class="message-content">{{ selectedMessage.content }}</p>
        <div class="button-group">
          <button @click="replyToMessage" class="primary-button">Antworten</button>
          <button @click="deleteMessage" class="delete-button">Löschen</button>
        </div>
      </div>

      <div v-else>
        <div class="form-group">
          <label for="organization">Organization:</label>
          <select id="organization" v-model="selectedOrganization" @change="fetchUsers" class="form-select">
            <option v-for="org in organizations" :key="org.org_id" :value="org.org_id">
              {{ org.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="recipient">To:</label>
          <select id="recipient" v-model="newMessageData.recipients" multiple @input="scrollToFirstMatch"
            class="form-select">
            <option v-for="user in organizationUsers" :key="user.id" :value="user.id">{{ user.username }}</option>
          </select>
        </div>

        <div class="form-group">
          <input type="text" v-model="newMessageData.subject" placeholder="Subject" class="form-input" />
        </div>

        <div class="form-group">
          <textarea v-model="newMessageData.content" placeholder="Type your message here..." class="form-textarea"
            rows="6"></textarea>
        </div>

        <button @click="sendMessage" class="primary-button">Senden</button>
      </div>

      <div v-if="isReplying" class="reply-section">
        <h4>Reply to: {{ selectedMessage.sender_name }}</h4>
        <div class="form-group">
          <textarea v-model="replyContent" placeholder="Type your reply here..." class="form-textarea"
            rows="6"></textarea>
        </div>
        <div class="button-group">
          <button @click="sendReply" class="primary-button">Antwort senden</button>
          <button @click="isReplying = false" class="secondary-button">Abbrechen</button>
        </div>
      </div>
    </section>

    <!-- Delete Confirmation Popup -->
    <div v-if="showDeletePopup" class="dialog-overlay">
      <div class="dialog-box">
        <p>Möchten Sie diese Nachricht löschen?</p>
        <div class="dialog-buttons">
          <button @click="confirmDelete" class="delete-button">Ja</button>
          <button @click="cancelDelete" class="secondary-button">Zurück</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';

export default {
  data() {
    return {
      messages: [],
      selectedMessage: null,
      isReplying: false,
      showPopup: false,
      popupMessage: '',
      newMessageData: { recipients: [], subject: '', content: '' },
      replyContent: '',
      selectedOrganization: '',
      organizations: [],
      organizationUsers: [],
      userId: null,
      showDeletePopup: false,
      messageToDelete: null,
    };
  },

  created() {
    // Get userId from localStorage
    this.userId = parseInt(localStorage.getItem('userId'));
  },

  mounted() {
    // Fetch all data on mount
    this.fetchOrganizations();
    this.fetchMessages();
    this.fetchAllUsers();
  },

  methods: {
    async fetchOrganizations() {
      try {
        this.organizations = await apiService.fetchOrganizations();
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    },

    async fetchMessages() {
      try {
        const userId = localStorage.getItem('userId');
        const messages = await apiService.fetchMessages(userId);
        this.messages = messages.map(message => ({
          ...message,
          sender_name: message.sender_name || 'Unknown',
          recipient_name: message.recipient_name || 'Unknown'
        }));
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    },

    async fetchAllUsers() {
      try {
        const users = await apiService.fetchUsers();
        this.organizationUsers = Array.isArray(users) ? users : [];
      } catch (error) {
        console.error("Error fetching users:", error);
        this.organizationUsers = [];
      }
    },

    getUserName(userId) {
      const user = this.organizationUsers.find(user => user.id === userId);
      return user ? user.username : "You";
    },

    getRecipientName(recipientId) {
      const recipient = this.organizationUsers.find(user => user.id === recipientId);
      return recipient ? recipient.username : "Unknown";
    },

    selectMessage(msg) {
      this.selectedMessage = msg;
      this.isReplying = false;
      this.replyContent = '';
    },

    newMessage() {
      this.selectedMessage = null;
      this.isReplying = false;
      this.newMessageData = { recipients: [], subject: '', content: '' };
    },

    replyToMessage() {
      this.isReplying = true;
    },

    scrollToFirstMatch(event) {
      const char = event.target.value[0];
      const option = [...event.target.options].find(opt => opt.text.startsWith(char));
      if (option) {
        option.selected = true;
        event.target.scrollTo(0, option.offsetTop);
      }
    },

    async sendMessage() {
      try {
        await apiService.sendMessage({
          sender_id: this.userId,
          recipients: this.newMessageData.recipients,
          subject: this.newMessageData.subject,
          content: this.newMessageData.content
        });

        this.showCustomPopup("Message sent successfully!");
        this.newMessage();
        this.fetchMessages();
      } catch (error) {
        console.error("Error sending message:", error);
      }
    },

    async sendReply() {
      try {
        if (!this.selectedMessage) return;

        const previousMessageContent = `
\n\n\n----------
\nDate: ${this.selectedMessage.created_at}
\nSubject: ${this.selectedMessage.subject}
\n\n${this.selectedMessage.content}
        `;

        const fullContent = `${this.replyContent}\n\n${previousMessageContent}`;

        await apiService.replyToMessage(this.selectedMessage.msg_id, {
          sender_id: this.userId,
          content: fullContent
        });

        this.showCustomPopup("Reply sent successfully!");
        this.isReplying = false;
        this.replyContent = '';
        this.fetchMessages();
      } catch (error) {
        console.error("Error sending reply:", error);
      }
    },

    async confirmDelete() {
      try {
        this.showCustomPopup("Message deleted successfully!");
        this.selectedMessage = null;
        this.showDeletePopup = false;
        await this.fetchMessages();
      } catch (error) {
        console.error("Error deleting message:", error);
        this.showCustomPopup("Error deleting message");
      }
    },

    cancelDelete() {
      this.showDeletePopup = false;
      this.messageToDelete = null;
    },

    deleteMessage() {
      this.messageToDelete = this.selectedMessage;
      this.showDeletePopup = true;
    },

    showCustomPopup(message) {
      this.popupMessage = message;
      this.showPopup = true;
    },

    closePopup() {
      this.showPopup = false;
      this.popupMessage = '';
    }
  }
};
</script>

<style scoped>
.message-page {
  display: flex;
  height: 100vh;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
  color: #404040;
}

.message-list {
  width: 30%;
  background-color: #f8f9fa;
  padding: 15px;
  border-right: 1px solid #e8e9f5;
  overflow-y: auto;
}

.message-list h3 {
  margin-bottom: 15px;
  color: #404040;
  font-weight: 600;
  font-size: 1.2rem;
}

.message-list ul {
  list-style: none;
  padding: 0;
}

.message-list li {
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 0.9em;
  line-height: 1.3;
  transition: all 0.2s ease;
  background-color: white;
}

.message-list li p {
  margin: 4px 0;
}

.message-to {
  border-left: 3px solid #4e9e81;
}

.message-from {
  border-left: 3px solid #4a90e2;
}

.message-list li:hover {
  background-color: #f0f0f0;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.message-view {
  width: 70%;
  padding: 25px;
  background-color: white;
}

.message-view textarea,
.message-view input,
.message-view select {
  width: 100%;
  margin: 0;
  padding: 12px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  color: #404040;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 15px;
  border-bottom: 1px solid #e8e9f5;
  margin-bottom: 15px;
}

.button-group {
  display: flex;
  gap: 15px;
  margin-top: 15px;
}

.primary-button {
  background-color: #4e9e81;
  color: white;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.2s ease;
}

.primary-button:hover {
  background-color: #3e8d70;
}

.secondary-button {
  background-color: #f6f6f6;
  color: #404040;
  border: 1px solid #e0e0e0;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.secondary-button:hover {
  background-color: #e8e8e8;
}

.delete-button {
  background-color: #f8e9eb;
  color: #963034;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.2s ease;
}

.delete-button:hover {
  background-color: #f5dfe1;
}

.message-details {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e8e9f5;
  margin-bottom: 20px;
}

.message-field {
  display: flex;
  margin-bottom: 10px;
}

.label {
  width: 80px;
  font-weight: 500;
  color: #505050;
}

.value {
  flex: 1;
  color: #404040;
}

.message-content {
  white-space: pre-wrap;
  color: #404040;
  margin-top: 15px;
  line-height: 1.5;
  padding: 15px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #e8e9f5;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #505050;
}

.form-select {
  width: 100%;
  padding: 10px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  color: #404040;
  background-color: white;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  color: #404040;
}

.form-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  color: #404040;
  resize: vertical;
  min-height: 120px;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #4e9e81;
}

.reply-section {
  margin-top: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e8e9f5;
}

.reply-section h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #404040;
}

/* Dialog Styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.dialog-box {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  width: 90%;
}

.dialog-box p {
  margin-bottom: 20px;
  font-size: 16px;
  color: #404040;
}

.dialog-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
}

/* Responsive styles */
@media (max-width: 1024px) {
  .message-page {
    flex-direction: column;
    height: auto;
  }

  .message-list,
  .message-view {
    width: 100%;
    height: auto;
  }

  .message-list {
    max-height: 300px;
    border-right: none;
    border-bottom: 1px solid #e8e9f5;
  }
}

@media (max-width: 768px) {
  .button-group {
    flex-direction: column;
    gap: 10px;
  }

  .dialog-buttons {
    flex-direction: column;
    gap: 10px;
  }

  .dialog-box {
    padding: 20px;
    width: 95%;
  }

  .message-field {
    flex-direction: column;
  }

  .label {
    width: 100%;
    margin-bottom: 4px;
  }
}
</style>
